"use client";

import { useEffect, useRef, useState } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Region = {
  code: string;
  name: string;
  x: number;
  y: number;
  status: "active" | "expansion" | "primary";
  az: number;
  latency: number;
};
const regions: Region[] = [{
  code: "us-east-1",
  name: "N. Virginia",
  x: 230,
  y: 175,
  status: "active",
  az: 6,
  latency: 92
}, {
  code: "us-west-2",
  name: "Oregon",
  x: 130,
  y: 165,
  status: "active",
  az: 4,
  latency: 118
}, {
  code: "ca-central",
  name: "Montreal",
  x: 240,
  y: 145,
  status: "active",
  az: 3,
  latency: 88
}, {
  code: "sa-east",
  name: "Sao Paulo",
  x: 305,
  y: 320,
  status: "active",
  az: 3,
  latency: 142
}, {
  code: "eu-west",
  name: "Dublin",
  x: 440,
  y: 145,
  status: "primary",
  az: 3,
  latency: 24
}, {
  code: "eu-central",
  name: "Frankfurt",
  x: 480,
  y: 155,
  status: "active",
  az: 3,
  latency: 32
}, {
  code: "eu-north",
  name: "Stockholm",
  x: 490,
  y: 115,
  status: "active",
  az: 3,
  latency: 41
}, {
  code: "uk-south",
  name: "London",
  x: 455,
  y: 135,
  status: "active",
  az: 3,
  latency: 28
}, {
  code: "me-central",
  name: "Dubai",
  x: 565,
  y: 220,
  status: "active",
  az: 3,
  latency: 88
}, {
  code: "ap-southeast",
  name: "Singapore",
  x: 690,
  y: 285,
  status: "active",
  az: 3,
  latency: 156
}, {
  code: "ap-northeast",
  name: "Tokyo",
  x: 765,
  y: 195,
  status: "active",
  az: 4,
  latency: 178
}, {
  code: "ap-south",
  name: "Mumbai",
  x: 620,
  y: 240,
  status: "active",
  az: 3,
  latency: 102
}, {
  code: "oceania",
  name: "Sydney",
  x: 770,
  y: 360,
  status: "active",
  az: 3,
  latency: 198
}, {
  code: "africa-south",
  name: "Cape Town",
  x: 510,
  y: 365,
  status: "active",
  az: 3,
  latency: 168
}, {
  code: "us-central",
  name: "Iowa",
  x: 195,
  y: 175,
  status: "expansion",
  az: 3,
  latency: 105
}, {
  code: "ap-east",
  name: "Hong Kong",
  x: 715,
  y: 235,
  status: "expansion",
  az: 3,
  latency: 165
}, {
  code: "eu-south",
  name: "Milan",
  x: 475,
  y: 175,
  status: "expansion",
  az: 3,
  latency: 48
}];
const certifications = ["ISO 9001", "QUALITY ASSURED", "ITLS CERTIFIED", "EXCELLENCE STD", "GLOBAL COMPLIANT"];
export default function Reach() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const [activeCount, setActiveCount] = useState(0);
  const targetActive = regions.filter(r => r.status === "active" || r.status === "primary").length;
  useEffect(() => {
    if (!isVisible) return;
    const duration = 1100;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setActiveCount(Math.round(eased * targetActive));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible, targetActive]);
  return <section data-section-id="19772" id="global-reach" className="bg-background text-foreground py-24 md:py-32" style={{
    ["--signal" as string]: "hsl(235 85% 60%)"
  }}>
      <div className="mx-auto w-full max-w-[1320px] px-5 md:px-10">
        {}
        <div ref={ref} className={`transition-all duration-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
          <div className="h-px w-full bg-border origin-left transition-transform duration-700 ease-out" style={{
          transform: isVisible ? "scaleX(1)" : "scaleX(0)"
        }} />
          <div className="mt-6 font-mono text-[11px] md:text-xs font-medium tracking-[0.08em] uppercase text-muted-foreground">
            {"02 / ITLS OPERATIONAL FOOTPRINT"}
          </div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {}
          <div className="md:col-span-4 order-2 md:order-1">
            <h2 className="font-default font-medium tracking-tight leading-[1.05] text-foreground" style={{
            fontSize: "clamp(2rem, 3.4vw, 3.25rem)",
            letterSpacing: "-0.02em"
          }}>
              Strategic presence across global markets.
            </h2>

            <p className="mt-6 text-muted-foreground font-default font-normal leading-[1.55]" style={{
            fontSize: "clamp(1rem, 1.15vw, 1.125rem)"
          }}>
              {"ITLS delivers precision-engineered consulting and project management services, ensuring seamless operational excellence regardless of your geographic location or regulatory environment."}
            </p>

            {}
            <div className="mt-10 space-y-3">
              <div className="font-mono text-[11px] font-medium tracking-[0.08em] uppercase text-muted-foreground pb-3 border-b border-border">
                {"LEGEND"}
              </div>

              <div className="flex items-center gap-3 py-1">
                <span className="inline-block size-2 rounded-lg" style={{
                backgroundColor: "var(--signal)"
              }} aria-hidden />
                <span className="font-mono text-xs tracking-[0.08em] uppercase text-foreground">
                  {"HQ HUB"}
                </span>
              </div>

              <div className="flex items-center gap-3 py-1">
                <span className="inline-block size-2 rounded-lg bg-muted-foreground/80" aria-hidden />
                <span className="font-mono text-xs tracking-[0.08em] uppercase text-muted-foreground">
                  {"ACTIVE OFFICE"}
                </span>
              </div>

              <div className="flex items-center gap-3 py-1">
                <span className="inline-block size-2 rounded-lg border border-muted-foreground/80" aria-hidden />
                <span className="font-mono text-xs tracking-[0.08em] uppercase text-muted-foreground">
                  {"PLANNED EXPANSION"}
                </span>
              </div>
            </div>

            {}
            <div className="mt-10 grid grid-cols-2 gap-px bg-border border border-border">
              <div className="bg-card p-5">
                <div className="font-mono text-[11px] tracking-[0.08em] uppercase text-muted-foreground">
                  {"ACTIVE"}
                </div>
                <div className="mt-2 font-mono font-medium text-foreground tabular-nums" style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                letterSpacing: "-0.04em",
                lineHeight: 1
              }}>
                  {String(activeCount).padStart(2, "0")}
                </div>
              </div>
              <div className="bg-card p-5">
                <div className="font-mono text-[11px] tracking-[0.08em] uppercase text-muted-foreground">
                  {"EXPANSION"}
                </div>
                <div className="mt-2 font-mono font-medium text-foreground tabular-nums" style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                letterSpacing: "-0.04em",
                lineHeight: 1
              }}>
                  {"03"}
                </div>
              </div>
            </div>

            {}
            <div className="mt-8 flex flex-wrap gap-2">
              {certifications.map((c, i) => <span key={c} data-index={i} className="font-mono text-[10px] md:text-[11px] tracking-[0.08em] uppercase text-muted-foreground border border-border px-2.5 py-1.5 rounded-md">
                  {c}
                </span>)}
            </div>
          </div>

          {}
          <div className="md:col-span-8 order-1 md:order-2">
            <TooltipProvider delayDuration={100}>
              {}
              <div className="hidden md:block">
                <MapPanel regions={regions} />
              </div>

              {}
              <div className="md:hidden">
                <ScrollArea className="w-full border border-border bg-card" style={{
                height: 360
              }}>
                  <div style={{
                  width: 900,
                  height: 360
                }}>
                    <MapPanel regions={regions} mobile />
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>
            </TooltipProvider>
          </div>
        </div>
      </div>
    </section>;
}
function MapPanel({
  regions,
  mobile = false
}: {
  regions: Region[];
  mobile?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  return <div ref={containerRef} className="relative w-full border border-border bg-card" style={mobile ? {
    width: 900,
    height: 360
  } : {
    aspectRatio: "900 / 480"
  }}>
      <svg viewBox="0 0 900 480" className="w-full h-full block" preserveAspectRatio="xMidYMid meet" aria-label="Global office map">
        {}
        <g fill="hsl(220 12% 14%)" stroke="hsl(220 10% 20%)" strokeWidth="1" strokeLinejoin="round">
          {}
          <path d="M80,110 L150,95 L210,100 L255,120 L265,150 L285,165 L275,200 L240,225 L200,235 L165,225 L130,210 L105,180 L90,150 Z" />
          {}
          <path d="M200,235 L240,245 L255,265 L240,280 L215,275 L195,255 Z" />
          {}
          <path d="M255,275 L300,275 L325,305 L320,345 L300,385 L280,400 L265,385 L260,350 L255,315 Z" />
          {}
          <path d="M425,110 L480,100 L510,115 L515,145 L495,170 L470,180 L445,175 L425,160 L415,135 Z" />
          {}
          <path d="M455,195 L510,195 L545,220 L555,265 L540,310 L515,350 L495,385 L475,385 L455,355 L445,310 L445,255 Z" />
          {}
          <path d="M515,180 L560,185 L585,210 L575,235 L545,235 L520,220 Z" />
          {}
          <path d="M510,90 L620,80 L730,85 L800,100 L810,140 L760,150 L680,145 L600,140 L540,135 L510,115 Z" />
          {}
          <path d="M595,215 L640,210 L660,240 L645,275 L620,275 L600,250 Z" />
          {}
          <path d="M665,255 L720,255 L735,285 L710,310 L680,300 L665,280 Z" />
          {}
          <path d="M730,160 L780,165 L795,195 L775,215 L750,210 L735,190 Z" />
          {}
          <path d="M720,340 L790,335 L815,365 L795,395 L750,395 L720,375 Z" />
          {}
          <path d="M340,75 L385,70 L395,95 L375,115 L350,110 L335,90 Z" />
        </g>

        {}
        <g stroke="hsl(220 10% 20%)" strokeWidth="1" strokeDasharray="2 4" opacity="0.5">
          <line x1="0" y1="240" x2="900" y2="240" />
          <line x1="450" y1="0" x2="450" y2="480" />
        </g>
      </svg>

      {}
      <div className="absolute inset-0">
        {regions.map((r, i) => {
        const xPct = r.x / 900 * 100;
        const yPct = r.y / 480 * 100;
        const isPrimary = r.status === "primary";
        const isExpansion = r.status === "expansion";
        return <div key={r.code} data-index={i} className="absolute" style={{
          left: `${xPct}%`,
          top: `${yPct}%`,
          transform: "translate(-50%, -50%)"
        }}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label={`${r.code} office`} onMouseEnter={() => setHovered(r.code)} onMouseLeave={() => setHovered(null)} onFocus={() => setHovered(r.code)} onBlur={() => setHovered(null)} className="relative flex items-center justify-center outline-none" style={{
                width: 18,
                height: 18
              }}>
                    {isPrimary ? <>
                        <span aria-hidden className="absolute rounded-lg" style={{
                    width: 16,
                    height: 16,
                    backgroundColor: "var(--signal)",
                    opacity: 0.25,
                    animation: "qh-pulse 2.4s ease-in-out infinite"
                  }} />
                        <span aria-hidden className="relative block" style={{
                    width: 8,
                    height: 8,
                    backgroundColor: "var(--signal)"
                  }} />
                      </> : isExpansion ? <span aria-hidden className="block" style={{
                  width: 6,
                  height: 6,
                  border: "1px solid hsl(220 8% 52%)",
                  backgroundColor: "transparent"
                }} /> : <span aria-hidden className="block" style={{
                  width: 6,
                  height: 6,
                  backgroundColor: "hsl(220 8% 52%)"
                }} />}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-popover text-popover-foreground border border-border font-mono text-[11px] tracking-[0.02em] rounded-md p-0">
                  <div className="px-3 py-2">
                    <div className="text-foreground uppercase">{r.code}</div>
                    <div className="mt-1 text-muted-foreground">
                      {"TEAM SIZE \u2014 "}{r.az}
                    </div>
                    <div className="text-muted-foreground">
                      {"LOCAL TIME \u2014 "}{r.latency}{"min"}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>

              {}
              <span className="absolute font-mono text-foreground/70 pointer-events-none whitespace-nowrap" style={{
            fontSize: 10,
            left: 14,
            top: -2,
            color: hovered === r.code ? "hsl(235 85% 60%)" : "hsl(220 8% 60%)"
          }}>
                {r.code}
              </span>
            </div>;
      })}
      </div>

      <style>{`
        @keyframes qh-pulse {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.1; transform: scale(1.6); }
        }
      `}</style>
    </div>;
}